/**
 * Staff session state. Sessions live in sessionStorage (cleared when the tab closes) under
 * `birflypermit.session` — pilots never have one; staff get one from `api.call({action:'login'})`.
 */
import { signal, computed } from '@preact/signals';
import type { Role } from '@birflypermit/shared';

export interface StoredSession {
  token: string;
  user: string;
  role: Role;
  displayName: string;
  exp: number; // ms epoch
}

const STORAGE_KEY = 'parwaaz.session';

function readStored(): StoredSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    if (!parsed.exp || parsed.exp <= Date.now()) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export const session = signal<StoredSession | null>(readStored());

export const isSignedIn = computed(() => session.value !== null);

export function setSession(next: StoredSession) {
  session.value = next;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // sessionStorage unavailable — session still works for this page load, just won't persist a reload
  }
}

export function signOut() {
  session.value = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function hasRole(...roles: Role[]): boolean {
  const current = session.value;
  return current !== null && roles.includes(current.role);
}
